import { Component, OnInit, OnChanges, TemplateRef, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { LoaderService } from 'src/app/loader.service';
import { AlertDialogService } from 'src/app/shared/alert-dialog/alert-dialog.service';
import { SharedService } from 'src/app/shared/shared.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';
import { SubscriptionService } from '../../subscription.service';
import { environment } from 'src/environments/environment';
import Utils from 'src/app/utils/utils';
import { MatDialog } from '@angular/material';
import { AuthService } from 'src/app/auth.service';
import { ruleTypeLabel as labelForRuleType } from '../../subscription.applicable-types';
import { subscriptionCreateSuccessAlert, subscriptionOperationFailureAlert } from '../../subscription.create-alert';

export interface PrepaidContext {
  city: { id: string, name: string };
  mode: { id: string, name: string };
  service: { id: string, name: string, serviceDetailId: string, active?: boolean };
  modeServices: { id: string, name: string, serviceDetailId: string, active?: boolean }[];
  categoryServices: { id: string, name: string, serviceDetailId: string, active?: boolean }[];
  skipServiceHierarchy: boolean;
  autoAssignmentTriggers: string[];
  allowSuspendServicesPostExpiry: boolean;
  alwaysSuspendServicesPostExpiry: boolean;
  allowDefaultSubscription: boolean;
  eligibleRuleTypes?: string[];
  displayTitles?: string[];
  exclusiveOfTax?: boolean;
}

@Component({
  selector: 'app-create-subscription',
  templateUrl: './prepaid.create.component.html',
  styleUrls: ['./prepaid.create.component.css']
})
export class CreateSubscriptionComponent implements OnInit, OnChanges {
  public subscriptionTypes: string[] = ["REGULAR", "CASHBACK"];
  public selectedSubscriptionType: string = "REGULAR";
  public hasCashbackPermission: boolean = false;

  @Input() context: PrepaidContext = null;
  get isQualityFeedbackSegment() {
    return this.formValue('targetSegment') === 'qualityFeedback';
  }

  get showQualityFeedbackSegment() {
    return this.selectedSubscriptionType !== 'CASHBACK';
  }
  private feedbackContextRequest = 0;
  private selectorRequest = 0;
  private qualityContextKey = '';
  private qualityContextReady = false;
  public feedbackContexts: string[] = [];
  public feedbackContextsLoading = false;
  public feedbackContextsError = false;
  public feedbackScoreApplicability = new FormArray([this.createFeedbackEntry()]);

  private createFeedbackEntry() {
    return new FormGroup({
      context: new FormControl(null),
      minValue: new FormControl(null),
      maxValue: new FormControl(null)
    });
  }

  ngOnChanges() {
    if (!this.context || !this.qualityContextReady) { return; }
    const key = this.getQualityContextKey();
    if (key === this.qualityContextKey) { return; }
    this.qualityContextKey = key;
    this.subscriptionFormGroup.get('userSelector').setValue(null, { emitEvent: false });
    this.userSelectors = [];
    this.applyPrepaidContext();
    this.feedbackScoreApplicability = new FormArray([this.createFeedbackEntry()]);
    this.loadFeedbackContexts();
  }

  private getQualityContextKey() {
    const context = this.context;
    return context ? [context.city.id, context.mode.id, context.service.id,
      context.categoryServices.map(service => service.serviceDetailId).join(',')].join(':') : '';
  }

  addFeedbackEntry() {
    this.feedbackScoreApplicability.push(this.createFeedbackEntry());
  }

  removeFeedbackEntry(index: number) {
    if (this.feedbackScoreApplicability.length > 1) {
      this.feedbackScoreApplicability.removeAt(index);
    }
  }

  async loadFeedbackContexts() {
    if (!this.isQualityFeedbackSegment || !this.context) {
      return;
    }
    const requestId = ++this.feedbackContextRequest;
    this.feedbackContextsLoading = true;
    this.feedbackContextsError = false;
    try {
      const response: any = await this.subsService.getFeedbackContexts(this.context.city.id, this.context.mode.id).toPromise();
      if (requestId !== this.feedbackContextRequest) { return; }
      this.feedbackContexts = (response && response.data) || [];
    } catch (error) {
      if (requestId !== this.feedbackContextRequest) { return; }
      this.feedbackContextsError = true;
      this.errorMessage('Could not load feedback contexts. Please retry.');
    } finally {
      if (requestId === this.feedbackContextRequest) {
        this.feedbackContextsLoading = false;
      }
    }
  }

  get showFeedbackFields() {
    const userSelector = this.subscriptionFormGroup.get('userSelector');
    return this.isQualityFeedbackSegment && userSelector.enabled
      && !!(userSelector.value && userSelector.value.id)
      && !this.formValue('isAutoAssignedToHH')
      && !this.formValue('isAutoAssignedToMC')
      && !this.formValue('isDefaultSubscription')
      && this.selectedSubscriptionType !== 'CASHBACK';
  }

  buildFeedbackScoreApplicability() {
    if (!this.showFeedbackFields) {
      return {};
    }
    if (this.feedbackContextsLoading || this.feedbackContextsError || !this.feedbackContexts.length) {
      throw new Error('Feedback contexts must be loaded before creating a rule');
    }
    const entries = this.feedbackScoreApplicability.value;
    if (!entries.length) {
      throw new Error('At least one feedback context is required');
    }
    const seen = new Set<string>();
    entries.forEach((entry, index) => {
      if (this.feedbackContexts.indexOf(entry.context) < 0 || seen.has(entry.context)) {
        throw new Error(`Select a unique feedback context for entry ${index + 1}`);
      }
      seen.add(entry.context);
      // The API requires both bounds to be nonzero; ratios are at most 1.
      if (typeof entry.minValue !== 'number' || typeof entry.maxValue !== 'number'
        || !Number.isFinite(entry.minValue) || !Number.isFinite(entry.maxValue)
        || entry.minValue <= 0 || entry.maxValue > 1 || entry.minValue > entry.maxValue) {
        throw new Error(`Feedback bounds must satisfy 0 < min value <= max value <= 1 for entry ${index + 1}`);
      }
    });
    return { feedbackScoreApplicability: entries };
  }

  @Input() launch: { city: string, service: string } = null;
  @Output() launchServiceUnavailable: EventEmitter<void> = new EventEmitter<void>();
  @Output() launchConsumed: EventEmitter<void> = new EventEmitter<void>();

  public subscriptionFormGroup = new FormGroup({
    ruleName: new FormControl(null),
    city: new FormControl(null),
    services: new FormControl(null),
    userSelector: new FormControl(null),
    priority: new FormControl(null),
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    excludeIncentiveTypes: new FormControl(null),
    ruleType: new FormControl(null),
    ruleValue: new FormControl(0),
    isAutoAssignedToHH: new FormControl(false),
    isAutoAssignedToMC: new FormControl(false),
    isDefaultSubscription: new FormControl(false),
    suspendServicesPostExpiry: new FormControl(false),
    targetSegment: new FormControl('userSelectors'),
    rules: new FormControl([])
  });

  public cities: { id: string, name: string; }[] = [];
  public services: {
    id: string,
    name: string,
    serviceDetailId: string;
  }[] = [];
  public isRecommended: false;
  public userSelectors: { id: string, name: string; }[] = [];
  public incentiveTypesMap = this.sharedService.INCENTIVE_TYPES_MAP;
  public incentiveTypes = Object.keys(this.incentiveTypesMap)
  public validityTypes: string[] = this.getValidityTypes();
  public maxRulesAtOnce: Number = environment.maxRulesCreationAtOnce;
  public today: Date = moment().startOf("hour").toDate();
  public tomorrowEod: Date = moment().endOf("day").minutes(30).add(1, 'days').toDate();
  public maxStartDate: Date;
  public minEndDate: Date = this.tomorrowEod;
  @ViewChild("OverlappingPriorityRuleAlert") OverlappingPriorityRuleAlert: TemplateRef<any>;
  public overlappingRules: any = [];
  public Utils = Utils;
  public subscriptionTitles: string[] = [];
  public eligibleRuleTypes: string[] = [];

  private initReady: Promise<any>;

  constructor(private sharedService: SharedService,
    private subsService: SubscriptionService,
    private loader: LoaderService,
    private alertService: AlertDialogService,
    public toasterService: ToasterService,
    private dialog: MatDialog,
    private authService: AuthService) {
    this.initializeCities();
  }

  private initializeCities() {
    this.loader.openLoading();
    this.initReady = this.fetchCities().finally(() => this.loader.closeLoading());
  }

  private async applyLaunchQueryPrefill() {
    const q = this.launch || { city: '', service: '' };
    const cityId = (q.city || '').trim();
    const serviceId = (q.service || '').trim();
    if (!cityId) {
      return;
    }

    try {
      await this.initReady;
      const city = this.cities.find(c => String(c.id) === String(cityId));
      if (!city) {
        this.errorMessage('City from launch URL is not available.');
        this.launchConsumed.emit();
        return;
      }

      this.subscriptionFormGroup.get('city').setValue(city, { emitEvent: false });
      await this.fetchServices();
      if (!serviceId) {
        this.launchConsumed.emit();
        return;
      }
      const matched = (this.services || []).filter(s => String(s.id) === String(serviceId));
      if (!matched.length) {
        this.launchServiceUnavailable.emit();
        return;
      }
      this.subscriptionFormGroup.get('services').setValue(matched);
      this.launchConsumed.emit();
    } catch (err) {
      console.error('Launch prefill failed', err);
      this.launchConsumed.emit();
    }
  }
  
  resetOnchangeOf(controlName: string, toReset: string[], cb = () => { }) {
    this.subscriptionFormGroup.get(controlName).valueChanges.subscribe(() => {
      toReset.forEach(control => {
        this.subscriptionFormGroup.get(control).setValue(null);
      });
      cb();
      this.handleSubscriptionCheckBoxFormControls();
    });
  }

   disableFormControl = (controlName: string) => {
    this.subscriptionFormGroup.get(controlName).setValue(null, { emitEvent: false });
    this.subscriptionFormGroup.get(controlName).disable({ emitEvent: false });
  };

   enableFormControl = (controlName: string) => {
    this.subscriptionFormGroup.get(controlName).enable({ emitEvent: false });
  };

  onSubscriptionTypeChange(type: string) {
    const suspendControl = this.subscriptionFormGroup.get('suspendServicesPostExpiry');
    this.enableFormControl('userSelector');
    suspendControl.setValue(false, { emitEvent: false });
    const mode: string = this.validateAndGetModeForServices() as string;
    if (this.canEnableHH(mode)) {
      this.enableFormControl('isAutoAssignedToHH');
    } else {
      this.disableFormControl('isAutoAssignedToHH');
    }
    if (this.canEnableMC()) {
      this.enableFormControl('isAutoAssignedToMC');
    } else {
      this.disableFormControl('isAutoAssignedToMC');
    }
    if (this.canEnableEntireCity()) {
      this.enableFormControl('isDefaultSubscription');
    } else {
      this.disableFormControl('isDefaultSubscription');
    }
    this.applyPrepaidContextFlags();
    this.applyEligibleRuleTypes();
    this.syncTargetSegmentAvailability();
  }

  async ngOnInit() {
    const permissions: string[] = await this.authService.getPermissions();
    this.hasCashbackPermission = permissions.includes('cashback_subscription_create');
    const modeBasedFormControlHandler = (mode: string) => {
      if (!this.canEnableHH(mode)) {
        this.disableFormControl('isAutoAssignedToHH');
      } else {
        this.enableFormControl('isAutoAssignedToHH');
      }

      if (!this.canEnableEntireCity()) {
        this.disableFormControl('isDefaultSubscription');
      } else {
        this.enableFormControl('isDefaultSubscription');
      }
  
      if (!this.canEnableMC()) {
        this.disableFormControl('isAutoAssignedToMC');
      } else {
        this.enableFormControl('isAutoAssignedToMC');
      }

      this.applyPrepaidContextFlags();
      this.applyEligibleRuleTypes();
      this.syncTargetSegmentAvailability();
    };

    this.resetOnchangeOf("city", ["services"], () => {
      this.services = [];
      this.fetchServices();
    });

    this.resetOnchangeOf("services", ["userSelector", "suspendServicesPostExpiry"], () => {
      this.userSelectors = [];
      this.fetchSelectors();
      // Re-apply ruleType derived from subscription type after services reset
      this.onSubscriptionTypeChange(this.selectedSubscriptionType);
      const mode: string = this.validateAndGetModeForServices() as string;
      modeBasedFormControlHandler(mode);
    });

    this.resetOnchangeOf("isAutoAssignedToMC", ["rules"], () => {
      this.addRule();
      const mode: string = this.validateAndGetModeForServices() as string;
      modeBasedFormControlHandler(mode);
      this.handleIsAutoAssignedToMC();
    });

    this.resetOnchangeOf("suspendServicesPostExpiry", ["rules"], () => {
      this.addRule();
      const mode: string = this.validateAndGetModeForServices() as string;
      modeBasedFormControlHandler(mode);
      if (this.formValue('isAutoAssignedToHH')) {
        this.handleIsAutoAssignedToHH();
      }
      if (this.formValue('isDefaultSubscription')) {
        this.handleIsDefaultSubscription();
      }
    });
  
    this.resetOnchangeOf("isAutoAssignedToHH", ["rules"], () => {
      this.addRule();
      const mode: string = this.validateAndGetModeForServices() as string;
      modeBasedFormControlHandler(mode);
      this.handleIsAutoAssignedToHH();
    });

    this.resetOnchangeOf("isDefaultSubscription", ["rules"], () => {
      this.addRule();
      const mode: string = this.validateAndGetModeForServices() as string;
      modeBasedFormControlHandler(mode);
      this.handleIsDefaultSubscription();
    });

    this.subscriptionFormGroup.get('isAutoAssignedToHH').disable({ emitEvent: false });
    this.subscriptionFormGroup.get('isAutoAssignedToMC').disable({ emitEvent: false });
    this.subscriptionFormGroup.get('isDefaultSubscription').disable({ emitEvent: false });
    this.subscriptionFormGroup.get('suspendServicesPostExpiry').disable({ emitEvent: false });

    this.subscriptionFormGroup.get('targetSegment').valueChanges.subscribe(() => {
      this.applyTargetSegment();
    });

    if (this.context) {
      this.applyPrepaidContext();
    } else {
      await this.applyLaunchQueryPrefill();
    }
    this.qualityContextReady = true;
    this.qualityContextKey = this.getQualityContextKey();
    await this.loadFeedbackContexts();
    if (!this.formValue('rules') || !this.formValue('rules').length) {
      this.addRule();
    }
  }

  blockInvalidNumberKeys(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  rejectNonPositiveAmount(event: Event, control) {
    const input = event.target as HTMLInputElement;
    if (input.value === '' || input.value == null) {
      return;
    }
    if (Number(input.value) <= 0) {
      control.setValue(null);
      input.value = '';
    }
  }

  serviceLabel(item) {
    return item.name + (item.active ? '' : ' (inactive)');
  }

  get modeServicesText() {
    if (!this.context || !this.context.modeServices) {
      return '';
    }
    return this.context.modeServices.map(service => this.serviceLabel(service)).join(', ');
  }

  get showEntireCitySegment() {
    return this.canEnableEntireCity();
  }

  get showHHSegment() {
    const mode = this.validateAndGetModeForServices() as string;
    return this.canEnableHH(mode);
  }

  get showMCSegment() {
    return this.canEnableMC();
  }

  private syncTargetSegmentAvailability() {
    const segment = this.formValue('targetSegment');
    if ((segment === 'entireCity' && !this.showEntireCitySegment)
      || (segment === 'HH' && !this.showHHSegment)
      || (segment === 'MC' && !this.showMCSegment)
      || (segment === 'qualityFeedback' && !this.showQualityFeedbackSegment)) {
      this.subscriptionFormGroup.get('targetSegment').setValue('userSelectors', { emitEvent: false });
      this.applyTargetSegment();
    }
  }

  private applyTargetSegment() {
    const segment = this.formValue('targetSegment') || 'userSelectors';
    const userSelector = this.formValue('userSelector');
    this.subscriptionFormGroup.get('isAutoAssignedToMC').setValue(segment === 'MC', { emitEvent: false });
    this.subscriptionFormGroup.get('isAutoAssignedToHH').setValue(segment === 'HH', { emitEvent: false });
    this.subscriptionFormGroup.get('isDefaultSubscription').setValue(segment === 'entireCity', { emitEvent: false });
    this.subscriptionFormGroup.get('rules').setValue(null);
    this.addRule();
    if (segment !== 'MC') {
      this.handleIsAutoAssignedToMC();
    }
    if (segment !== 'HH') {
      this.handleIsAutoAssignedToHH();
    }
    if (segment !== 'entireCity') {
      this.handleIsDefaultSubscription();
    }
    if (segment === 'MC') {
      this.handleIsAutoAssignedToMC();
    } else if (segment === 'HH') {
      this.handleIsAutoAssignedToHH();
    } else if (segment === 'entireCity') {
      this.handleIsDefaultSubscription();
    }
    this.handleSubscriptionCheckBoxFormControls();
    if ((segment === 'userSelectors' || segment === 'qualityFeedback') && userSelector) {
      this.subscriptionFormGroup.get('userSelector').setValue(userSelector, { emitEvent: false });
    }
    if (this.isQualityFeedbackSegment) {
      this.qualityContextKey = this.getQualityContextKey();
      this.loadFeedbackContexts();
    }
  }

  private canEnableHH(_mode?: string) {
    if (!this.context) {
      return false;
    }
    const triggers = this.context.autoAssignmentTriggers || [];
    return triggers.indexOf('captainFirstTimeActivation') !== -1;
  }

  private canEnableMC() {
    if (!this.context) {
      return false;
    }
    const triggers = this.context.autoAssignmentTriggers || [];
    return triggers.indexOf('captainMCSignup') !== -1;
  }

  private canEnableEntireCity() {
    return !!(this.context && this.context.allowDefaultSubscription);
  }

  private applyPrepaidContextFlags() {
    if (!this.context) {
      return;
    }
    if (this.canEnableHH(null)) {
      this.enableFormControl('isAutoAssignedToHH');
    } else {
      this.disableFormControl('isAutoAssignedToHH');
    }
    if (this.canEnableMC()) {
      this.enableFormControl('isAutoAssignedToMC');
    } else {
      this.disableFormControl('isAutoAssignedToMC');
    }
    if (this.canEnableEntireCity()) {
      this.enableFormControl('isDefaultSubscription');
    } else {
      this.disableFormControl('isDefaultSubscription');
    }
    const suspend = this.subscriptionFormGroup.get('suspendServicesPostExpiry');
    if (this.context.alwaysSuspendServicesPostExpiry) {
      suspend.setValue(true, { emitEvent: false });
      suspend.disable({ emitEvent: false });
      return;
    }
    if (this.context.allowSuspendServicesPostExpiry) {
      suspend.enable({ emitEvent: false });
      return;
    }
    suspend.setValue(false, { emitEvent: false });
    suspend.disable({ emitEvent: false });
  }

  get showBlockOrders() {
    if (!this.context) {
      return true;
    }
    return this.context.alwaysSuspendServicesPostExpiry === true || this.context.allowSuspendServicesPostExpiry === true;
  }

  private applyPrepaidContext() {
    const modeId = this.context.mode && this.context.mode.id;
    const withMode = (service) => Object.assign({}, service, { mode: service.mode || modeId });
    this.subscriptionFormGroup.get('city').setValue(this.context.city, { emitEvent: false });
    if (this.context.skipServiceHierarchy) {
      this.services = (this.context.modeServices || []).map(withMode);
      this.subscriptionFormGroup.get('services').setValue(this.services, { emitEvent: false });
    } else {
      this.services = (this.context.categoryServices || []).map(withMode);
      const selectedId = this.context.service && this.context.service.id;
      const selected = this.services.filter(service => String(service.id) === String(selectedId));
      this.subscriptionFormGroup.get('services').setValue(selected.length ? selected : [withMode(this.context.service)], { emitEvent: false });
    }
    this.fetchSelectors();
    this.applyPrepaidContextFlags();
    this.applyEligibleRuleTypes();
    this.applyDisplayTitles();
    this.syncTargetSegmentAvailability();
  }

  ruleTypeLabel(type: string) {
    return labelForRuleType(type);
  }

  get isRuleTypeLocked() {
    return this.eligibleRuleTypes.length === 1;
  }

  private applyEligibleRuleTypes() {
    const types = (this.context && this.context.eligibleRuleTypes) || [];
    this.eligibleRuleTypes = types.length ? types : ['zeroCommission'];
    const current = this.subscriptionFormGroup.get('ruleType').value;
    if (this.eligibleRuleTypes.indexOf(current) === -1) {
      this.subscriptionFormGroup.get('ruleType').setValue(this.eligibleRuleTypes[0], { emitEvent: false });
    }
    if (this.eligibleRuleTypes.length === 1) {
      this.subscriptionFormGroup.get('ruleType').disable({ emitEvent: false });
    } else {
      this.subscriptionFormGroup.get('ruleType').enable({ emitEvent: false });
    }
  }

  private applyDisplayTitles() {
    const titles = (this.context && this.context.displayTitles) || [];
    this.subscriptionTitles = Array.isArray(titles) ? titles : [];
  }

  get taxInfoText() {
    return this.context && this.context.exclusiveOfTax
      ? 'This amount is exclusive of tax'
      : 'This amount is inclusive of tax';
  }

  handleIsDefaultSubscription() {
    if (!this.formValue('isDefaultSubscription')) {
      this.maxRulesAtOnce = environment.maxRulesCreationAtOnce;
      this.subscriptionFormGroup.get('endDate').enable({ emitEvent: false });
      this.subscriptionFormGroup.get('userSelector').enable({ emitEvent: false });
      this.subscriptionFormGroup.get('priority').setValue(null, { emitEvent: false });
      this.subscriptionFormGroup.get('priority').enable({ emitEvent: false });
    } else {
      this.maxRulesAtOnce = environment.maxRulesCreationAtOnce;
      this.subscriptionFormGroup.get('endDate').setValue(null, { emitEvent: false });
      this.subscriptionFormGroup.get('endDate').disable({ emitEvent: false });
      this.disableFormControl('userSelector');
      this.subscriptionFormGroup.get('priority').setValue(0, { emitEvent: false });
      this.subscriptionFormGroup.get('priority').disable({ emitEvent: false });
    }
  }

  handleIsAutoAssignedToMC() {
    if (this.formValue('isAutoAssignedToMC')) {
      this.maxRulesAtOnce = 1;
      this.disableFormControl('endDate');
      this.disableFormControl('userSelector');
      this.disableFormControl('priority');
      this.disableFormControl('excludeIncentiveTypes');
      this.subscriptionFormGroup.get('rules').value.map(rule => {
        rule.periodInDays.setValue(null, { emitEvent: false })
        rule.periodInDays.disable({ emitEvent: false })
        rule.ruleAmount.setValue(0, { emitEvent: false })
        rule.ruleAmount.disable({ emitEvent: false });
      })
    } else {
      this.enableFormControl('endDate');
      this.enableFormControl('priority');
      this.enableFormControl('excludeIncentiveTypes');
      this.enableFormControl('userSelector');
    }
  }

  handleIsAutoAssignedToHH() {
    if (!this.formValue('isAutoAssignedToHH')) {
      this.maxRulesAtOnce = environment.maxRulesCreationAtOnce;
      this.subscriptionFormGroup.get('endDate').enable({ emitEvent: false });
      this.subscriptionFormGroup.get('userSelector').enable({ emitEvent: false });
    } else {
      this.maxRulesAtOnce = 1;
      this.subscriptionFormGroup.get('endDate').setValue(null, { emitEvent: false });
      this.subscriptionFormGroup.get('endDate').disable({ emitEvent: false });
      this.disableFormControl('userSelector');
      this.subscriptionFormGroup.get('rules').value.map(rule => {
        rule.ruleAmount.setValue(0, { emitEvent: false })
        rule.ruleAmount.disable({ emitEvent: false });
      })
    }
  }


  handleSubscriptionCheckBoxFormControls() {
    if (!this.services) {
      this.disableFormControl('isAutoAssignedToMC');
      this.disableFormControl('isAutoAssignedToHH');
      this.disableFormControl('isDefaultSubscription');
      this.disableFormControl('suspendServicesPostExpiry');
    }

    if (this.formValue("isDefaultSubscription")) {
      this.disableFormControl('isAutoAssignedToMC');
      this.disableFormControl('isAutoAssignedToHH');
    }

    if (this.formValue("isAutoAssignedToHH")) {
      this.disableFormControl('isAutoAssignedToMC');
      this.disableFormControl('isDefaultSubscription');
    }

    if (this.formValue("isAutoAssignedToMC")) {
      this.disableFormControl('isAutoAssignedToHH');
      this.disableFormControl('isDefaultSubscription');
      this.disableFormControl('suspendServicesPostExpiry');
    }

    if (this.formValue("suspendServicesPostExpiry")) {
      this.disableFormControl('isAutoAssignedToMC');
    }
  }
  

  formValue(name) {
    return this.subscriptionFormGroup.get(name).value;
  }

  fetchSelectors() {
    const requestId = ++this.selectorRequest;
    this.userSelectors = [];
    const city = this.formValue("city");
    const services = this.formValue("services");
    if (city && services) {
      const serviceTypes = services
        .map(s => s.serviceDetailId).join(",");
      this.loader.openLoading();
      this.sharedService
        .fetchRules(city.id, serviceTypes, null, "rider")
        .toPromise()
        .then((res: any) => {
          if (requestId !== this.selectorRequest) { return; }
          this.userSelectors = res.data.selectors
            .map(({ _id, name }) => ({ id: _id, name }));
        })
        .finally(() => this.loader.closeLoading());
    }
  }

  private fetchServices(): Promise<void> {
    const city = this.formValue("city");
    if (!city) {
      return Promise.resolve();
    }
    this.loader.openLoading();
    return this.sharedService
      .fetchServicesWithMode(city.id)
      .toPromise()
      .then((res: any) => {
        this.services = res.data
          .filter(serviceDetail => serviceDetail.active)
          .map(({ service, _id, modeInfo }) => ({
            id: service._id,
            name: service.name,
            mode: modeInfo._id,
            serviceDetailId: _id
          }));
      })
      .finally(() => this.loader.closeLoading());
  }

  private fetchCities(): Promise<void> {
    return this.sharedService
      .fetchCities()
      .toPromise()
      .then((res: any) => {
        this.cities = res.data.cities
          .map(({ _id, displayName }) => ({ id: _id, name: displayName }));
      })
  }

  changeInDate() {
    const startDate = this.formValue("startDate");

    if (startDate) {
      this.minEndDate = startDate;
    }

    const endDate = this.formValue("endDate");
    if (endDate) {
      this.maxStartDate = endDate;
    }
  }

  async createSubscriptionRules() {
    this.loader.openLoading();
    try {
      const rules = await this.buildRules();
      if (!rules) {
        return;
      }

      const payload = {
        type: this.formValue('isAutoAssignedToMC') ? "MC" : this.selectedSubscriptionType,
        rules: rules,
      };

      let resp: any = await this.subsService.createSubscriptionRules(payload).toPromise();
      this.alertService.open(subscriptionCreateSuccessAlert(resp, { configRequestKind: 'subscription' }));
    } catch (error) {
      this.alertService.open(subscriptionOperationFailureAlert(error));
    } finally {
      this.loader.closeLoading();
    }
  }

  alertMessage(alert: { title: string, message: string }) {
    this.alertService.open(alert)
  }

  errorMessage(message) {
    this.toasterService.showToaster(new Toaster({
      type: ToasterType.WARNING,
      message: message
    }));
  }

  validateField(fieldName: string, message: string) {
    const value = this.formValue(fieldName);
    if (!value) {
      this.errorMessage(message);
      throw new Error(message);
    }
    return value;
  }

  async buildRules() {
    const feedbackFields = this.buildFeedbackScoreApplicability();
    const ruleName = this.validateField("ruleName", "Rule name is required");
    const city = this.validateField("city", "City is required");
    const services = this.validateField("services", "Services is required");
    const mode: string = this.validateAndGetModeForServices() as string;
    const isAutoAssignedToHH: boolean = this.formValue('isAutoAssignedToHH');
    const isAutoAssignedToMC: boolean = this.formValue('isAutoAssignedToMC');
    const isDefaultSubscription: boolean = this.formValue('isDefaultSubscription');
    const suspendServicesPostExpiry: boolean = this.selectedSubscriptionType === 'CASHBACK' || this.formValue('suspendServicesPostExpiry');
    const startTime = moment(this.validateField("startDate", "Purchase Start date is required").getTime()).unix();
    const endTime = !(isAutoAssignedToHH || isAutoAssignedToMC || isDefaultSubscription) ? moment(this.validateField("endDate", "Purchase  End date is required").getTime()).unix() : null;
    const skipUserSelector = isAutoAssignedToHH || isAutoAssignedToMC || isDefaultSubscription || this.selectedSubscriptionType === 'CASHBACK';
    const userSelector = !skipUserSelector ? this.validateField("userSelector", "User selector is required") : null;
    const excludeIncentiveTypes = this.formValue("excludeIncentiveTypes");
    const ruleType = this.validateField("ruleType", "Rule type is required");
    const ruleValue = this.formValue("ruleValue") || 0;
    const serviceDetailIds = services.map(s => s.serviceDetailId);
    let priority = null;
    if (isAutoAssignedToMC) {
      priority = null; 
    } else if (isDefaultSubscription) {
      priority = 0;
    } else {
      priority = this.validateField("priority", "Priority is required");
    }

    if (this.formValue('rules').length < 1) {
      this.errorMessage("At least one rule is required");
    }
    
    let recommendedCount = this.countRecommendedRules();

    if(recommendedCount>1){
      this.errorMessage("Only one rule with Recommended tag is allowed");
      this.alertMessage({
        title: "Error",
        message: "Only one rule with Recommended tag is allowed",
      })
      return;
    }

    if (!(isAutoAssignedToHH || isAutoAssignedToMC || isDefaultSubscription)) {
      try {
        let rules: any = await this.subsService.getSamePriorityRules(serviceDetailIds, startTime, endTime, [priority]).toPromise();
        if (rules && rules.length > 0) {
          this.overlappingRules = rules
          this.dialog.open(this.OverlappingPriorityRuleAlert);
          return
        }
      } catch(error) {
        this.alertMessage({
          title: "Error",
          message: JSON.stringify(error.message),
        })
        return
      }
    }

    if (isAutoAssignedToHH) {
      let rules: any = await this.subsService.getHighestPriorityHHRule(serviceDetailIds).toPromise();
      let higherPriorityRules = rules.filter(rule => rule.priority >= priority)
      if (higherPriorityRules.length > 0) {
        this.overlappingRules = higherPriorityRules;
        this.dialog.open(this.OverlappingPriorityRuleAlert);
        return
      }
    }

    if (isDefaultSubscription) {
      let rules: any = await this.subsService.getHighestPriorityDefaultRule(serviceDetailIds).toPromise();
      if (rules.some(rule => rule.priority >= priority)) {
        this.overlappingRules = rules;
        this.dialog.open(this.OverlappingPriorityRuleAlert);
        return
      }
    }

    let commonFields = {
      ...feedbackFields,
      ruleName,
      startTime: startTime,
      endTime: endTime,
      priority: !isAutoAssignedToMC ? Number(priority) : 0,
      city: city.id,
      services: services.map(s => s.id),
      serviceDetails: serviceDetailIds,
      mode,
      isAutoAssignedToHH,
      isAutoAssignedToMC,
      isDefaultSubscription,
      exclusiveOfTax: !!(this.context && this.context.exclusiveOfTax),
      userSelectors: !skipUserSelector ? [userSelector.id] : [],
      userSelectorName: !skipUserSelector ? userSelector.name : null,
      excludeIncentiveTypes: excludeIncentiveTypes || [],
      ruleType,
      suspendServicesPostExpiry,
      rule: [this.getRuleConstruct(ruleType, ruleValue)],
    };

    return this.formValue('rules').map((rule, index) => {

      if (!rule.validityType.value) {
        this.errorMessage(`validity type is required for rule ${index + 1}`);
        throw new Error(`validity type is required for rule ${index + 1}`);
      }

      if (rule.validityType.value === 'dapr' && !rule.minValue.value) {
        this.errorMessage(`min value is required for dapr validity in rule ${index + 1}`);
        throw new Error(`min value is required for dapr validity in rule ${index + 1}`);
      }

      if (!rule.maxValue.value && rule.validityType.value !== 'unlimited' && rule.validityType.value !== 'dapr') {
        this.errorMessage(`max value is required for rule ${index + 1}`);
        throw new Error(`max value is required for rule ${index + 1}`);
      }

      const isAutoAssigned = isAutoAssignedToHH || isAutoAssignedToMC;
      
      if (!isAutoAssigned && rule.ruleAmount.value <= 0) {
        this.errorMessage(`purchase price is required and must have a value greater than zero for rule ${index + 1}`);
        throw new Error(`purchase price is required and must have a value greater than zero for rule ${index + 1}`);
      } 

      if (isAutoAssigned && rule.ruleAmount.value < 0) {
        this.errorMessage(`invalid purchase price for rule ${index + 1}`);
        throw new Error(`invalid purchase price for rule ${index + 1}`);
      }
      

      if (rule.strikedOutAmount.value && rule.strikedOutAmount.value <= rule.ruleAmount.value) {
        this.errorMessage(`Original price should be greater than purchase price for rule ${index + 1}`);
        throw new Error(`Original price should be greater than purchase price for rule ${index + 1}`);
      }

      if (!isAutoAssignedToMC && !rule.periodInDays.value) {
        this.errorMessage(`period in days is required for rule ${index + 1}`);
        throw new Error(`period in days is required for rule ${index + 1}`);
      }

      const validityObj: any = { type: rule.validityType.value };
      if (rule.maxValue.value != null && rule.maxValue.value !== '') {
        validityObj.maxValue = rule.maxValue.value;
      }
      if (rule.minValue.value != null && rule.minValue.value !== '') {
        validityObj.minValue = rule.minValue.value;
      }

      return {
        ...commonFields,
        validity: [validityObj],
        ruleAmount: rule.ruleAmount.value,
        strikedOutAmount: rule.strikedOutAmount.value,
        periodInDays: rule.periodInDays.value,
        ruleTitle: rule.subscriptionTitle.value,
        isRecommended: rule.isRecommended.value || false,
      }
    })
  }


  getRuleConstruct(ruleType: string, ruleValue: string | null) {
    switch (ruleType) {
      case "variableCommission":
        const value = Number(ruleValue);
        if (!value || value > 100) {
          const message = "Rule value should be between 0 to 100";
          this.errorMessage(message);
          throw new Error(message);
        }
        return {
          variable: "commission",
          operator: "-",
          value: value,
          type: "percent"
        };
      case "zeroCommission":
      case "qualityReset":
        return {
          variable: "commission",
          operator: "=",
          value: 0,
          type: "flat"
        };
    }
  }

  addRule() {
    let defaultRuleElement = {
      validityType: new FormControl(null),
      maxValue: new FormControl(null),
      minValue: new FormControl(null),
      ruleAmount: new FormControl(null),
      strikedOutAmount: new FormControl(null),
      periodInDays: new FormControl(null),
      subscriptionTitle: new FormControl(null),
      isRecommended: new FormControl(false)
    }
    if (this.formValue('rules')) {
      this.formValue('rules').push(defaultRuleElement)
    } else {
      this.subscriptionFormGroup.get('rules').setValue([defaultRuleElement])
    }
  }
  
  countRecommendedRules() {
    let rulesArray = this.subscriptionFormGroup.get('rules').value
    let recommendTag= 0;

    rulesArray.forEach((element, index)=>{
      if(element.isRecommended.value){
        recommendTag++;
      }
    })
    return recommendTag;
  }

  deleteRule(index) {
    this.formValue('rules').splice(index, 1)
  }

  validityTypeChanged(rule) {
    if (rule.validityType.value === 'unlimited') {
      rule.maxValue.setValue(null);
      rule.maxValue.disable();
      rule.minValue.setValue(null);
      rule.minValue.disable();
    } else if (rule.validityType.value === 'dapr') {
      rule.maxValue.setValue(null);
      rule.maxValue.disable();
      rule.minValue.enable();
    } else {
      rule.maxValue.enable();
      rule.minValue.setValue(null);
      rule.minValue.disable();
    }
  }

  validateAndGetModeForServices() {
    if (!this.formValue('services')) {
      return
    }
    let distinctModes = new Set(this.formValue('services').map((service: { mode: string }) => service.mode))

    if ( distinctModes.size > 1 ) {
      this.subscriptionFormGroup.get('services').setValue(null)
      this.subscriptionFormGroup.get('ruleType').setValue(null)
      this.alertMessage({
        title: "Error",
        message: "Please select services with same mode"
      });
      return;
    }

    let mode = distinctModes.values().next().value;

    return mode;
  }

  getValidityTypes() {
    return environment.validityTypesEnabled.length > 0 ? environment.validityTypesEnabled : ["unlimited"]
  }

  getValidityTypesForSelectedType(): string[] {
    if (this.selectedSubscriptionType === 'CASHBACK' || this.formValue('ruleType') === 'qualityReset') {
      return ['dapr'];
    }
    return this.validityTypes.filter(type => type !== 'dapr');
  }
}
